import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { formatMoney } from '../../theme/chart';
import { parseLocalDate } from '../../utils/bills';
import { mixWithWhite } from '../../utils/color';
import { Goal } from '../../services/goalApi';
import { GROWTH_MONTHS, GoalOutlook, GrowthOutlook } from './goalOutlook';
import { BalancePoint } from './goalMonths';
import { ChartLegend, LegendItem } from './ChartLegend';
import { AxisLabel, AxisLabels, SectionLabel } from './goalUi';

interface Props {
  goal: Goal;
  outlook: GoalOutlook;
  /** the balance so far, oldest first, ending today */
  history: BalancePoint[];
  /** an event goal's average deposit over the last months */
  average: number;
  /** an event goal's months to the target at that average */
  arrivalMonths: number | null;
  color: string;
  currency: string;
  now?: Date;
}

const W = 480;
const H = 190;
const L = 8;
const R = 472;
const DAY = 24 * 60 * 60 * 1000;
const MONTH_DAYS = 30.44;

const fmt = (value: number) => value.toFixed(1);

/** The month as the axis says it: "mar", "Mar". */
const shortMonth = (date: Date, locale: string) => date.toLocaleDateString(locale, { month: 'short' }).replace('.', '');

/**
 * A habit's capital over ten years, with and without the quoted yield: the
 * curves sit together for years and then open, which is the point.
 */
const GrowthChart: React.FC<{ outlook: GrowthOutlook; color: string }> = ({ outlook, color }) => {
  const { t, i18n } = useTranslation('planning');
  const locale = i18n.language;
  const T = 14;
  const B = 176;
  const main = outlook.grown ?? outlook.flat;
  const yMax = Math.max(main[GROWTH_MONTHS], 1) * 1.04;
  const x = (month: number) => L + ((R - L) * month) / GROWTH_MONTHS;
  const y = (value: number) => B - ((B - T) * value) / yMax;
  const line = (series: number[]) => series.map((value, month) => `${month ? 'L' : 'M'}${fmt(x(month))},${fmt(y(value))}`).join(' ');
  const rate = outlook.quotedRate.toLocaleString(locale, { maximumFractionDigits: 3 });

  const legend: LegendItem[] = [
    {
      key: 'main',
      label: outlook.grown
        ? t(outlook.ratePeriod === 'monthly' ? 'goals.projections.withYieldMonthly' : 'goals.projections.withYieldYearly', { rate })
        : t('goals.chart.depositsOnly'),
      swatch: 'line',
      color,
    },
  ];
  if (outlook.grown) legend.push({ key: 'flat', label: t('goals.projections.withoutYield'), swatch: 'dash', color: tokens.ink3 });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      <SectionLabel>{t('goals.chart.growthTitle')}</SectionLabel>
      <Box>
        <Box component='svg' viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', height: 'auto', display: 'block' }} role='img' aria-label={t('goals.chart.growthLabel') as string}>
          <line x1={L} x2={R} y1={B} y2={B} stroke={tokens.line} />
          <path d={`${line(main)} L${fmt(x(GROWTH_MONTHS))},${B} L${fmt(x(0))},${B} Z`} fill={mixWithWhite(color, 0.9)} />
          {outlook.grown && <path d={line(outlook.flat)} fill='none' stroke={tokens.ink3} strokeWidth={2} strokeDasharray='4 4' />}
          <path d={line(main)} fill='none' stroke={color} strokeWidth={3} strokeLinejoin='round' />
          {outlook.grown && <circle cx={x(GROWTH_MONTHS)} cy={y(outlook.flat[GROWTH_MONTHS])} r={4} fill={tokens.ink3} />}
          <circle cx={x(GROWTH_MONTHS)} cy={y(main[GROWTH_MONTHS])} r={5} fill={color} />
        </Box>
        <AxisLabels
          labels={[
            { key: 'today', text: t('goals.projections.today'), at: 0 },
            { key: 'five', text: t('goals.projections.horizon5'), at: 0.5 },
            { key: 'ten', text: t('goals.projections.horizon10'), at: 1 },
          ]}
        />
      </Box>
      <ChartLegend items={legend} />
    </Box>
  );
};

/**
 * An event goal's path: the balance so far, then two dashed roads to the
 * target line from today — the pace it has, in the goal's color, and the pace
 * the deadline asks for, in grey. The deadline is a vertical line; the
 * arrival a ring, amber when it comes after the deadline.
 */
const PathChart: React.FC<Omit<Props, 'outlook'> & { outlook: GoalOutlook }> = ({
  goal,
  outlook,
  history,
  average,
  arrivalMonths,
  color,
  currency,
  now = new Date(),
}) => {
  const { t, i18n } = useTranslation('planning');
  const locale = i18n.language;
  const T = 26;
  const B = 176;

  const target = Math.max(0, Number(goal.targetAmount ?? 0));
  const principal = Math.max(0, Number(goal.currentAmount) || 0);
  const start = history[0]?.date ?? now;
  const pastDays = Math.max((now.getTime() - start.getTime()) / DAY, 0);

  const deadline = goal.targetDate ? parseLocalDate(goal.targetDate) : null;
  const deadlineDays = deadline && deadline > now ? (deadline.getTime() - now.getTime()) / DAY : 0;
  const projecting = !goal.isAchieved && target > principal;
  const arrivalDays = projecting && arrivalMonths != null ? arrivalMonths * MONTH_DAYS : null;
  // A far-off arrival should not flatten everything else: past this cap the
  // pace line runs off the edge instead.
  const cap = Math.max(deadlineDays * 2, 365);
  let futureDays = Math.max(deadlineDays, Math.min(arrivalDays ?? 0, cap));
  if (futureDays === 0) futureDays = Math.max(pastDays * 0.12, 14);
  const spanDays = (pastDays + futureDays * 1.06) || 1;

  const xDays = (daysFromNow: number) => L + ((R - L) * (pastDays + daysFromNow)) / spanDays;
  const xDate = (date: Date) => xDays((date.getTime() - now.getTime()) / DAY);
  const yMax = Math.max(target, ...history.map((point) => point.value), 1);
  const y = (value: number) => B - ((B - T) * Math.min(value, yMax)) / yMax;
  // The labels' box is exactly as wide as the drawing, so a share of W is a share of it.
  const at = (xValue: number) => xValue / W;

  const todayX = xDays(0);
  const endDays = spanDays - pastDays;
  const points = history.map((point) => `${fmt(xDate(point.date))},${fmt(y(point.value))}`);

  const needed = outlook?.kind === 'path' ? outlook : null;
  const behind = needed ? needed.behind : false;
  const arrivalShown = arrivalDays != null && arrivalDays <= endDays;
  const paceEnd = arrivalDays == null
    ? null
    : arrivalShown
      ? { x: xDays(arrivalDays), value: target }
      : { x: R, value: principal + (average * endDays) / MONTH_DAYS };
  const arrivalDate = arrivalMonths != null ? new Date(now.getFullYear(), now.getMonth() + Math.ceil(arrivalMonths), 1) : null;
  const arrivalTone = behind ? tokens.warn : tokens.accent;

  const labels: AxisLabel[] = [{ key: 'today', text: t('goals.projections.today'), at: at(todayX), color: tokens.ink, bold: true }];
  if (deadlineDays > 0) labels.push({ key: 'deadline', text: t('goals.chart.deadline'), at: at(xDays(deadlineDays)) });
  if (arrivalShown && arrivalDate && arrivalDays) {
    labels.push({ key: 'arrival', text: shortMonth(arrivalDate, locale), at: at(xDays(arrivalDays)), color: arrivalTone, bold: true });
  }

  const legend: LegendItem[] = [];
  if (projecting && paceEnd) legend.push({ key: 'pace', label: t('goals.chart.legendPace'), swatch: 'dash', color });
  if (projecting && needed && deadlineDays > 0) legend.push({ key: 'needed', label: t('goals.chart.legendNeeded'), swatch: 'dash', color: tokens.ink3 });

  const targetLabelTop = `${(y(target) / H) * 100}%`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      <SectionLabel>{t('goals.chart.pathTitle')}</SectionLabel>
      <Box>
        <Box sx={{ position: 'relative' }}>
          <Box component='svg' viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', height: 'auto', display: 'block' }} role='img' aria-label={t('goals.chart.pathLabel') as string}>
            <line x1={L} x2={R} y1={B} y2={B} stroke={tokens.line} />
            {target > 0 && <line x1={L} x2={R} y1={y(target)} y2={y(target)} stroke={tokens.accent} strokeDasharray='4 4' />}
            {deadlineDays > 0 && (
              <line x1={xDays(deadlineDays)} x2={xDays(deadlineDays)} y1={y(target)} y2={B} stroke='#C9D2D0' strokeDasharray='3 4' />
            )}
            {points.length > 1 && (
              <>
                <path d={`M${points.join(' L')} L${fmt(todayX)},${B} L${fmt(xDate(start))},${B} Z`} fill={mixWithWhite(color, 0.85)} />
                <polyline points={points.join(' ')} fill='none' stroke={color} strokeWidth={3} strokeLinejoin='round' />
              </>
            )}
            {projecting && needed && deadlineDays > 0 && (
              <line x1={todayX} y1={y(principal)} x2={xDays(deadlineDays)} y2={y(target)} stroke={tokens.ink3} strokeWidth={2} strokeDasharray='4 4' />
            )}
            {projecting && paceEnd && (
              <line x1={todayX} y1={y(principal)} x2={paceEnd.x} y2={y(paceEnd.value)} stroke={color} strokeWidth={2.5} strokeDasharray='5 5' />
            )}
            <circle cx={todayX} cy={y(principal)} r={5} fill={color} />
            {projecting && arrivalShown && paceEnd && (
              <circle cx={paceEnd.x} cy={y(target)} r={5} fill={tokens.surface} stroke={arrivalTone} strokeWidth={2.5} />
            )}
          </Box>
          {target > 0 && (
            <Box
              component='span'
              aria-hidden
              sx={{ position: 'absolute', left: `${(L / W) * 100}%`, top: targetLabelTop, transform: 'translateY(calc(-100% - 2px))', fontSize: 12, fontWeight: 700, color: tokens.accent, lineHeight: '16px' }}
            >
              {formatMoney(target, currency, locale)}
            </Box>
          )}
        </Box>
        <AxisLabels labels={labels} />
      </Box>
      {legend.length > 0 && <ChartLegend items={legend} />}
    </Box>
  );
};

/**
 * The main chart of a goal's panel: a habit's growth, or an event goal's
 * path to its target. Habits without anything to project draw nothing.
 */
export const GoalChart: React.FC<Props> = (props) => {
  if (props.outlook?.kind === 'growth') {
    return <GrowthChart outlook={props.outlook} color={props.color} />;
  }
  if (props.goal.goalType === 'recurring') {
    return null;
  }
  return <PathChart {...props} />;
};
